import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

export default function MyWorlds() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [worlds, setWorlds] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const { data } = await api.get("/custom-worlds/mine");
      setWorlds(data);
    } catch (e) {
      toast.error("Failed to load your worlds");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const remove = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm("Delete this world? Existing adventures using it will break.")) return;
    try {
      await api.delete(`/custom-worlds/${id}`);
      setWorlds(worlds.filter((w) => w.world_id !== id));
    } catch (err) {
      toast.error("Delete failed");
    }
  };

  const togglePublish = async (w, e) => {
    e.stopPropagation();
    if (!user.premium) {
      toast.error("Publishing requires Sovereign");
      return;
    }
    try {
      await api.post(`/custom-worlds/${w.world_id}/${w.is_published ? "unpublish" : "publish"}`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Publish failed");
    }
  };

  const playDraft = async (w, e) => {
    e.stopPropagation();
    try {
      const { data } = await api.post("/adventures/start", {
        world_id: w.world_id, version_id: "v_default", hard_mode: false,
      });
      navigate(`/play/${data.adventure.adventure_id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Play failed");
    }
  };

  return (
    <div data-testid="my-worlds-page" className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="font-mono text-[0.6rem] uppercase tracking-[0.4em] gold mb-1">My Forge</div>
          <h2 className="font-serif-display text-2xl sm:text-3xl">Worlds you have crafted</h2>
        </div>
        <button data-testid="new-world-btn" onClick={() => navigate("/worlds/new")} className="btn-gold">Forge New World</button>
      </div>

      {loading ? (
        <div className="font-mono text-xs text-stone-500 animate-pulse">Loading forge...</div>
      ) : worlds.length === 0 ? (
        <div className="tome-card rounded-sm p-8 text-center font-serif-body text-stone-400 space-y-3">
          <p>You have not forged a world yet.</p>
          <button onClick={() => navigate("/worlds/new")} className="btn-gold">Forge your first world</button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {worlds.map((w) => (
            <div
              key={w.world_id}
              data-testid={`myworld-card-${w.world_id}`}
              onClick={() => navigate(`/worlds/${w.world_id}`)}
              className="tome-card rounded-sm p-5 cursor-pointer flex flex-col gap-3"
            >
              <div className="flex items-start justify-between">
                <div>
                  <div className="font-mono text-[0.6rem] uppercase tracking-widest gold mb-1">{w.mechanics_style.replace(/_/g, " ")}</div>
                  <div className="font-serif-display text-lg leading-tight">{w.title}</div>
                  <div className="font-serif-body italic text-stone-400 text-sm">{w.tagline}</div>
                </div>
                <button onClick={(e) => remove(w.world_id, e)} className="text-stone-600 hover:text-red-400 text-sm">✕</button>
              </div>
              <div className="flex flex-wrap gap-2 font-mono text-[0.6rem] uppercase tracking-widest">
                {w.is_published ? (
                  <span className="px-2 py-0.5 border border-emerald-600/50 text-emerald-300 rounded-sm">◆ Published</span>
                ) : (
                  <span className="px-2 py-0.5 border border-stone-600 text-stone-400 rounded-sm">Draft</span>
                )}
                <span className="px-2 py-0.5 border border-amber-500/30 gold rounded-sm">{w.play_count} plays</span>
                {w.rating_count > 0 && (
                  <span className="px-2 py-0.5 border border-amber-500/30 gold rounded-sm">{w.rating_avg} ★ ({w.rating_count})</span>
                )}
              </div>
              <div className="flex gap-2 mt-auto">
                <button data-testid={`play-draft-${w.world_id}`} onClick={(e) => playDraft(w, e)} className="btn-ghost text-[0.65rem] flex-1">Play</button>
                <button
                  data-testid={`publish-toggle-${w.world_id}`}
                  onClick={(e) => togglePublish(w, e)}
                  className={`btn-ghost text-[0.65rem] flex-1 ${w.is_published ? "border-emerald-500/50 text-emerald-300" : ""}`}
                >
                  {w.is_published ? "Unpublish" : "Publish"}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
